import type { Application } from '@hotwired/stimulus';
import type { Options, mount, Result } from '../../../assets/js/vendor/ocr-client/ocr-client.es';
import { Controller } from '@hotwired/stimulus';
import { getCameraDevices, hasCameraPermissions } from '../../500-common/util';
import { clearToastAlerts } from '../alerts/alerts';

type HasKeys<T> = {
    [P in keyof T]: any;
};

type TesseractClientMountOptions = Options;

type TesseractScannerValues = HasKeys<
    TesseractClientMountOptions & {
        endpoint: string;
        saveEndpoint?: string;
    }
>;

/**
 * Stimulus Wrapper for the 1000grad OCR Client for the Tesseract Server scanning
 */
export class TesseractScanner extends Controller implements TesseractScannerValues {
    static values: Omit<TesseractScannerValues, 'onResult'> = {
        startCameraManually: {
            type: Boolean,
            default: false,
        },
        startScanManually: {
            type: Boolean,
            default: false,
        },
        showProblematicCharacters: {
            type: Boolean,
            default: false,
        },
        scanDelayMs: {
            type: Number,
            default: 3000,
        },
        showCameraToggle: {
            type: Boolean,
            default: false,
        },
        showResultField: {
            type: Boolean,
            default: false, // Per default, this one cannot be overwritten in the frontend. We set this always to false and add our own input field.
        },
        endpoint: {
            type: String,
            default: '',
        },
        saveEndpoint: {
            type: String,
            default: '',
        },
    };

    static targets = [
        'noPermissions',
        'noCamera',
        'permissionRequest',
        'scanner',
        'result',
        'retry',
        'scanning',
        'resultFieldInput',
        'resultTypeFieldInput',
        'resultFileFieldInput',
    ];

    static vendorRoot = '';

    private ocrClient: ReturnType<typeof mount>;
    private selectorId: string;
    private fileReader: FileReader;

    get startScanManually(): TesseractScannerValues['startScanManually'] {
        // @ts-ignore
        return this.startScanManuallyValue;
    }

    set startScanManually(value: boolean) {
        // @ts-ignore
        this.startScanManuallyValue = value;
    }

    get startCameraManually(): TesseractScannerValues['startCameraManually'] {
        // @ts-ignore
        return this.startCameraManuallyValue;
    }
    get showProblematicCharacters(): TesseractScannerValues['showProblematicCharacters'] {
        // @ts-ignore
        return this.showProblematicCharactersValue;
    }
    get showCameraToggle(): TesseractScannerValues['showCameraToggle'] {
        // @ts-ignore
        return this.showCameraToggleValue;
    }
    get scanDelayMs(): TesseractScannerValues['scanDelayMs'] {
        // @ts-ignore
        return this.scanDelayMsValue;
    }
    get showResultField(): TesseractScannerValues['showResultField'] {
        // @ts-ignore
        return this.showResultFieldValue;
    }

    /**
     * Tesseract Server Endpoint
     */
    get endpoint(): TesseractScannerValues['endpoint'] {
        // @ts-ignore
        return this.endpointValue;
    }

    /**
     * Tesseract Server Endpoint for training function
     */
    get saveEndpoint(): TesseractScannerValues['saveEndpoint'] {
        // @ts-ignore
        return this.saveEndpointValue;
    }

    /**
     * Return all options
     */
    get options(): TesseractClientMountOptions {
        const {
            startCameraManually,
            startScanManually,
            showProblematicCharacters,
            showCameraToggle,
            scanDelayMs,
            showResultField,
        } = this;

        const handleResult = (result: Result) => this.onResult(result);

        return {
            startCameraManually,
            startScanManually,
            showProblematicCharacters,
            showCameraToggle,
            scanDelayMs,
            showResultField,
            onResult: handleResult,
        };
    }

    get scannerElement(): HTMLElement {
        // @ts-ignore
        return this.scannerTarget;
    }

    get noPermissionsElement(): HTMLElement {
        // @ts-ignore
        return this.noPermissionsTarget;
    }

    get permissionRequestElement(): HTMLElement {
        // @ts-ignore
        return this.permissionRequestTarget;
    }

    get noCameraElement(): HTMLElement {
        // @ts-ignore
        return this.noCameraTarget;
    }

    get scanningElement(): HTMLElement {
        // @ts-ignore
        return this.scanningTarget;
    }

    get resultElement(): HTMLElement {
        // @ts-ignore
        return this.hasResultTarget ? this.resultTarget : null;
    }

    get resultFieldInputElement(): HTMLInputElement {
        // @ts-ignore
        return this.hasResultFieldInputTarget ? this.resultFieldInputTarget : null;
    }

    get resultSourceFieldInputElement(): HTMLInputElement {
        // @ts-ignore
        return this.hasResultTypeFieldInputTarget ? this.resultTypeFieldInputTarget : null;
    }

    get resultFileFieldInputElement(): HTMLInputElement {
        // @ts-ignore
        return this.hasResultFileFieldInputTarget ? this.resultFileFieldInputTarget : null;
    }

    get retryElement(): HTMLInputElement {
        // @ts-ignore
        return this.hasRetryTarget ? this.retryTarget : null;
    }

    async connect(): Promise<void> {
        this.permissionRequestElement.hidden = false;
        this.fileReader = new FileReader();
        this.initFileReader();

        const devices = await getCameraDevices();

        if (!devices?.length) {
            // No video inputs
            this.permissionRequestElement.hidden = true;
            this.noCameraElement.hidden = false;
        } else if (!(await hasCameraPermissions())) {
            // Video inputs, but we don't have permissions
            this.permissionRequestElement.hidden = true;
            this.noPermissionsElement.hidden = false;
        } else {
            if (this.resultFieldInputElement.value.trim() !== '') {
                if (this.retryElement) {
                    this.retryElement.hidden = true;
                }

                this.setResultMode();
                this.mount(this.options);
            } else {
                await this.mount(this.options);
                this.setScanMode();
            }
        }
    }

    initFileReader() {
        this.fileReader.addEventListener(
            'load',
            () => {
                // Convert image file to base64 string
                this.resultFileFieldInputElement.value = this.fileReader.result as string;
            },
            false
        );
    }

    async mount(options: TesseractClientMountOptions) {
        if (!this.selectorId) {
            this.selectorId = this.selectorId || `scanner-${Math.random().toString(16).slice(2)}`;
            this.scannerElement.setAttribute('id', this.selectorId);
        }

        try {
            const { mount } = await import(`../../../assets/js/vendor/ocr-client/ocr-client.es.js`);

            this.ocrClient = mount(`#${this.selectorId}`, this.endpoint, options);
        } catch (e) {
            console.error(e);
        }
    }

    onResult(result: Result) {
        const file = new File(['foo'], 'foo.txt', {
            type: 'text/plain',
        });

        if (result?.text) {
            if (result?.source === 'qr') {
                const path = new URL(result.text).pathname.substring(1).split('/');

                if (path[0] === 'licensekey') {
                    this.resultFieldInputElement.value = path[1];
                }
            } else {
                this.resultFieldInputElement.value = result.text;
            }

            this.resultSourceFieldInputElement.value = result?.source;

            if (result?.file) {
                // See initFileReader function
                this.fileReader.readAsDataURL(result.file);
            }

            this.dispatchUserKeyInputChange();
            this.setResultMode();
        } else {
            console.info(result);
        }
    }

    retry(e: Event) {
        e.preventDefault();

        this.resultFieldInputElement.value = '';
        this.resultFileFieldInputElement.value = '';
        this.dispatchUserKeyInputChange();
        this.setScanMode();
        this.ocrClient.startCamera();
    }

    disconnect(): void {
        if (this.ocrClient) {
            this.ocrClient.stopScan();
            this.ocrClient.stopCamera();
            this.ocrClient = null;
        }
    }

    private dispatchUserKeyInputChange() {
        const event = new InputEvent('input', {
            view: window,
            bubbles: true,
            cancelable: true,
        });

        this.resultFieldInputElement.dispatchEvent(event);
    }

    private setScanMode() {
        this.permissionRequestElement.hidden = true;
        this.noCameraElement.hidden = true;
        this.noPermissionsElement.hidden = true;
        this.scannerElement.hidden = false;
        this.scanningElement.hidden = false;

        if (this.ocrClient) {
            // Add time out to ensure stream is active a the time of starting the scan
            setTimeout(() => this.ocrClient.startScan(), 100);
        }

        if (this.resultElement) {
            this.resultElement.hidden = true;
        }

        clearToastAlerts();
    }

    private setResultMode() {
        this.permissionRequestElement.hidden = true;
        this.noCameraElement.hidden = true;
        this.noPermissionsElement.hidden = true;
        this.scannerElement.hidden = false;
        this.scanningElement.hidden = true;

        if (this.resultElement) {
            this.resultElement.hidden = false;
        }
    }

    static register(application: Application) {
        application.register('tesseract-scanner', this);
    }
}
